import type {
	CreatePropertyViewPayload,
	PropertyViewResponse,
	SyncOfflineViewsPayload,
	SyncOfflineViewsResponse,
	UpdatePropertyViewPayload
} from '~/composables/usePropertyViewTracking';
import type { ShortVideoProperty } from '~/core/entities/property';
import type { ResultTuple } from '~~/core/helpers/result';

import { type CookieData } from '~/composables/useCookieData';
import { Result } from '~~/core/helpers/result';
import { createOriginalErrorObject } from '~~/server/utils/errorHandling';

import { prepareCookieHeaders } from './prepareCookieHeaders';

export interface VideoHistoryApiReturn {
	videos: ShortVideoProperty[];
	pagination: {
		page: number;
		perPage: number;
		total: number;
		totalPages: number;
	};
}

interface NuxtPropertyViewsRepository {
	getVideoHistory(
		params: { page: number; perPage: number },
		cookies?: CookieData
	): Promise<ResultTuple<VideoHistoryApiReturn>>;
	createView(
		payload: CreatePropertyViewPayload,
		cookies?: CookieData
	): Promise<ResultTuple<PropertyViewResponse>>;
	updateViewEngagement(
		viewId: string,
		payload: UpdatePropertyViewPayload,
		cookies?: CookieData
	): Promise<ResultTuple<void>>;
	syncOfflineViews(
		payload: SyncOfflineViewsPayload,
		cookies?: CookieData
	): Promise<ResultTuple<SyncOfflineViewsResponse>>;
}

export const nuxtPropertyViewsRepository: NuxtPropertyViewsRepository = {
	async getVideoHistory(params, cookies) {
		try {
			const headers = prepareCookieHeaders(cookies);
			const response = await $fetch<VideoHistoryApiReturn>(
				'/api/property-views/video-history',
				{
					method: 'GET',
					query: params,
					headers
				}
			);

			return Result.ok(response);
		} catch (error) {
			return Result.error({
				message: 'Error while fetching video history from the API.',
				source: 'nuxtPropertyViewsRepository.getVideoHistory',
				originalErrorObject: createOriginalErrorObject(error)
			});
		}
	},

	async createView(payload, cookies) {
		try {
			const headers = prepareCookieHeaders(cookies);
			const response = await $fetch<PropertyViewResponse>(
				'/api/property-views',
				{
					method: 'POST',
					body: payload,
					headers
				}
			);

			return Result.ok(response);
		} catch (error) {
			return Result.error({
				message: 'Error while creating property view.',
				source: 'nuxtPropertyViewsRepository.createView',
				originalErrorObject: createOriginalErrorObject(error)
			});
		}
	},

	async updateViewEngagement(viewId, payload, cookies) {
		try {
			const headers = prepareCookieHeaders(cookies);
			await $fetch(`/api/property-views/${viewId}/engagement`, {
				method: 'PATCH',
				body: payload,
				headers
			});

			return Result.ok(undefined);
		} catch (error) {
			return Result.error({
				message: 'Error while updating property view engagement.',
				source: 'nuxtPropertyViewsRepository.updateViewEngagement',
				originalErrorObject: createOriginalErrorObject(error)
			});
		}
	},

	async syncOfflineViews(payload, cookies) {
		try {
			const headers = prepareCookieHeaders(cookies);
			const response = await $fetch<SyncOfflineViewsResponse>(
				'/api/property-views/sync-offline',
				{
					method: 'POST',
					body: payload,
					headers
				}
			);

			return Result.ok(response);
		} catch (error) {
			return Result.error({
				message: 'Error while syncing offline property views.',
				source: 'nuxtPropertyViewsRepository.syncOfflineViews',
				originalErrorObject: createOriginalErrorObject(error)
			});
		}
	}
};
